import { PreviewMediaType } from '@/types';

import type { LibraryItem, Preset } from '@/types';

/**
 * Builds a full `Preset` from a cached library grid item (and its section slug),
 * so the create flow can start without the extra `/flow-landings/slug` request.
 *
 * The grid item already carries the same `uploadItems`/`textItems` the detail
 * endpoint returns (same CMS transform). The two gaps we synthesize: the array
 * form of media (the item has a single `media`) and `category` (the section slug).
 *
 * A multi-output preset holds one preview per output in `mediaList`; the detail
 * endpoint returns the same set as its `image`/`video` array, so both routes
 * into the create flow produce the same preview. `media` is the fallback for a
 * cache written before the expand existed.
 */
const buildPresetFromLibraryItem = (
  item: LibraryItem,
  sectionSlug: string
): Preset => {
  const isVideo = item.type === PreviewMediaType.VIDEO;
  const previews = item.mediaList?.length ? item.mediaList : [item.media];

  return {
    documentId: item.documentId,
    flowId: item.flowId ?? '',
    presetCreditCount: item.presetCreditCount ?? null,
    // Both numbers ride top-level on the grid item (the CMS strips the internal
    // `flow` field and promotes them). Dropping `outputsCount` here would make
    // every run single-output on this path, which is the one the create flow
    // normally takes — the folder split would then only ever fire via the
    // slower `/flow-landings/slug` route.
    outputsCount: item.outputsCount ?? null,
    type: item.type,
    title: item.title,
    description: item.description,
    slug: item.slug,
    category: sectionSlug,
    textItems: item.textItems ?? [],
    uploadItems: item.uploadItems ?? [],
    hasEditOption: item.hasEditOption,
    image: isVideo ? [] : previews.map((media) => ({ media })),
    video: isVideo ? previews.map((media) => ({ media })) : [],
  };
};

export default buildPresetFromLibraryItem;
